import type React from "react";

import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { getActionButtonColorStyles } from "shared/atoms/BaseButton/BaseButton";

const Container = styled.div`
  max-width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-content: center;
  align-items: center;
  ${props => props.theme.font.link3};
  font-weight: 400;
`;

const LeftButton = styled.button`
  outline: none;
  border-radius: ${props => props.theme.borderRadius.basic} 0px 0px ${props => props.theme.borderRadius.basic};
  box-sizing: border-box;
  width: 40px;
  height: 40px;
  text-align: center;
  cursor: pointer;

  ${getActionButtonColorStyles()}
`;

const RightButton = styled(LeftButton)`
  position: relative;
  border-radius: 0px ${props => props.theme.borderRadius.basic} ${props => props.theme.borderRadius.basic} 0px;
`;

const Wrapper = styled.div`
  padding: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${props => props.theme.belowMobileBreakpoint} {
    display: none;
  }
`;

const LabelWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  height: 40px;
  min-width: 80px;
  box-sizing: border-box;
  text-align: center;
  border-top: 1px solid ${({ theme }) => theme.colors.redesign.db20};
  border-bottom: 1px solid ${({ theme }) => theme.colors.redesign.db20};
  user-select: none;
  text-transform: capitalize;
`;

const ButtonWrap = styled.div`
  ${props => props.theme.belowMobileBreakpoint} {
    margin-left: ${props => props.theme.spacing.S_5};
  }
  display: flex;
  flex-wrap: nowrap;
`;

interface Props {
  readonly totalCount?: number;
  readonly first?: number;
  readonly last?: number;
  readonly pageInfo?: {
    readonly hasNextPage?: boolean;
    readonly hasPreviousPage?: boolean;
  };
  readonly label?: string;
  readonly className?: string;
  readonly onPageChange?: (str: string) => void;
}

export const Pagination: React.FC<Props> = ({
  totalCount = 0,
  first = 0,
  last = 0,
  pageInfo = { hasNextPage: false, hasPreviousPage: false },
  onPageChange = () => undefined,
  label = null,
  className,
}) => {
  const { t } = useTranslation();

  const data = totalCount > 0 && first > 0 && last > 0;

  return (
    <Container data-testid="pagination" className={className}>
      {data && <Wrapper>{t("common.pagination", { first, last, totalCount })}</Wrapper>}
      <ButtonWrap>
        <LeftButton
          data-testid="pagination-previous"
          onClick={() => {
            if (pageInfo.hasPreviousPage) onPageChange("previous");
          }}
          disabled={!pageInfo.hasPreviousPage}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </LeftButton>
        {label && (
          <LabelWrapper>
            <Typography variant="subtitle1">{label}</Typography>
          </LabelWrapper>
        )}
        <RightButton
          data-testid="pagination-next"
          onClick={() => {
            if (pageInfo.hasNextPage) onPageChange("next");
          }}
          disabled={!pageInfo.hasNextPage}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </RightButton>
      </ButtonWrap>
    </Container>
  );
};
